// ==========================================
// Vite Native actions.ts proxy layer
// ==========================================

import { client, API_BASE, authFetch } from '../lib/rpc';

export async function fetchLiveEdgeSettings() {
    return await (await client.api.settings.$get()).json();
}
export async function fetchPublicSettings() {
    return await (await client.api['public-settings'].$get()).json();
}
export async function saveLiveEdgeSettings(settings: any) {
    return await (await client.api.settings.$post({ json: settings })).json();
}
export async function fetchCommioTestState() {
    return await (await client.api.testing.commio.$get()).json();
}
export async function saveCommioTestState(endpointId: string, isTested: boolean) {
    return await (await client.api.testing.commio.$post({ json: { endpointId, isTested } })).json();
}

export async function fetchCnamEndpoints() {
    const base = import.meta.env.DEV ? '' : API_BASE.replace(/\/$/, '');
    return await (await authFetch(`${base}/api/services/cnam-endpoints`)).json();
}

// Edge Domain URLs
export async function generateShortUrl(longUrl: string, options?: any) {
    const res = await client.api.shorten.$post({ json: { url: longUrl, ...options } });
    if(res.ok) {
        const body = await res.json();
        return `${window.location.origin}/s/${body.slug}`;
    }
    return longUrl;
}
export async function listShortUrls(params: any) {
    const queryPayload: any = {};
    Object.keys(params).forEach(k => { if (params[k]) queryPayload[k] = params[k]?.toString() });
    return await (await client.api.shorturls.$get({ query: queryPayload })).json();
}
export async function getShortUrlStats(slug: string) { return await (await client.api.shorten[':slug'].stats.$get({ param: { slug } })).json(); }
export async function updateShortUrl(slug: string, settings: any) { return await (await client.api.shorten[':slug'].settings.$put({ param: { slug }, json: settings })).json(); }
export async function deleteShortUrl(slug: string) { return await (await client.api.shorten[':slug'].$delete({ param: { slug } })).json(); }

// Unified Action Runner generic factory bridge
const createActionExecutor = (actionName: string) => async (payload: any = {}) => {
    try {
        const res = await client.api.actions.execute[':actionName'].$post({
            param: { actionName },
            json: payload
        });
        return await res.json();
    } catch(e: any) {
        return { error: e.message };
    }
}

// Unified Commio Proxies
export const getCompanies = createActionExecutor('getCompanies');
export const assignDidCompany = createActionExecutor('assignDidCompany');
export const assignMultipleDidsCompany = createActionExecutor('assignMultipleDidsCompany');
export const testCommioAddSmsIp = createActionExecutor('testCommioAddSmsIp');
export const testCommioConfirmPortInTicket = createActionExecutor('testCommioConfirmPortInTicket');
export const testCommioCoverage = createActionExecutor('testCommioCoverage');
export const testCommioCreateCnamProfile = createActionExecutor('testCommioCreateCnamProfile');
export const testCommioCreateIntlRouteBlock = createActionExecutor('testCommioCreateIntlRouteBlock');
export const testCommioCreateMessagingProfile = createActionExecutor('testCommioCreateMessagingProfile');
export const testCommioCreateNotification = createActionExecutor('testCommioCreateNotification');
export const testCommioCreateOriginationGroup = createActionExecutor('testCommioCreateOriginationGroup');
export const testCommioCreateProfile = createActionExecutor('testCommioCreateProfile');
export const testCommioCreateRouteBlock = createActionExecutor('testCommioCreateRouteBlock');
export const testCommioCreateRoutingProfile = createActionExecutor('testCommioCreateRoutingProfile');
export const testCommioCreateStaticProfile = createActionExecutor('testCommioCreateStaticProfile');
export const testCommioDeleteIntlRouteBlock = createActionExecutor('testCommioDeleteIntlRouteBlock');
export const testCommioDeleteProfile = createActionExecutor('testCommioDeleteProfile');
export const testCommioDeleteRouteBlock = createActionExecutor('testCommioDeleteRouteBlock');
export const testCommioDeleteStaticProfile = createActionExecutor('testCommioDeleteStaticProfile');
export const testCommioDeleteTimedProfile = createActionExecutor('testCommioDeleteTimedProfile');
export const testCommioDisableAutoBill = createActionExecutor('testCommioDisableAutoBill');
export const testCommioDownloadInboundCdrs = createActionExecutor('testCommioDownloadInboundCdrs');
export const testCommioDownloadMessagingMdrs = createActionExecutor('testCommioDownloadMessagingMdrs');
export const testCommioDownloadOutboundCdrs = createActionExecutor('testCommioDownloadOutboundCdrs');
export const testCommioE = createActionExecutor('testCommioE');
export const testCommioEnableAutoBill = createActionExecutor('testCommioEnableAutoBill');
export const testCommioFinalizeHostedNumberOrder = createActionExecutor('testCommioFinalizeHostedNumberOrder');
export const testCommioGetAvailablePortInNumbers = createActionExecutor('testCommioGetAvailablePortInNumbers');
export const testCommioGetBalance = createActionExecutor('testCommioGetBalance');
export const testCommioGetCarrierIds = createActionExecutor('testCommioGetCarrierIds');
export const testCommioGetCarriers = createActionExecutor('testCommioGetCarriers');
export const testCommioGetCountries = createActionExecutor('testCommioGetCountries');
export const testCommioGetCountryDetail = createActionExecutor('testCommioGetCountryDetail');
export const testCommioGetDailyInternationalSpend = createActionExecutor('testCommioGetDailyInternationalSpend');
export const testCommioGetFailedCalls = createActionExecutor('testCommioGetFailedCalls');
export const testCommioGetFailedCallsByDid = createActionExecutor('testCommioGetFailedCallsByDid');
export const testCommioGetHostedNumbers = createActionExecutor('testCommioGetHostedNumbers');
export const testCommioGetInboundCdrs = createActionExecutor('testCommioGetInboundCdrs');
export const testCommioGetIntlRouteBlocks = createActionExecutor('testCommioGetIntlRouteBlocks');
export const testCommioGetInvoicePayments = createActionExecutor('testCommioGetInvoicePayments');
export const testCommioGetInvoices = createActionExecutor('testCommioGetInvoices');
export const testCommioGetMdrs = createActionExecutor('testCommioGetMdrs');
export const testCommioGetMessagingMdrs = createActionExecutor('testCommioGetMessagingMdrs');
export const testCommioGetNotifications = createActionExecutor('testCommioGetNotifications');
export const testCommioGetNumberManagement = createActionExecutor('testCommioGetNumberManagement');
export const syncCommioDids = createActionExecutor('syncCommioDids');
export const getDids = createActionExecutor('getDids');
export const testCommioGetOriginationUsageMonth = createActionExecutor('testCommioGetOriginationUsageMonth');
export const testCommioGetOriginationUsageRange = createActionExecutor('testCommioGetOriginationUsageRange');
export const testCommioGetOutboundCdrs = createActionExecutor('testCommioGetOutboundCdrs');
export const testCommioGetPaymentInfo = createActionExecutor('testCommioGetPaymentInfo');
export const testCommioGetPortInDids = createActionExecutor('testCommioGetPortInDids');
export const testCommioGetPortInTrackingLogs = createActionExecutor('testCommioGetPortInTrackingLogs');
export const testCommioGetProfiles = createActionExecutor('testCommioGetProfiles');
export const testCommioGetProfilesBasic = createActionExecutor('testCommioGetProfilesBasic');
export const testCommioGetRateCenters = createActionExecutor('testCommioGetRateCenters');
export const testCommioGetRateDecks = createActionExecutor('testCommioGetRateDecks');
export const testCommioGetRouteBlock = createActionExecutor('testCommioGetRouteBlock');
export const testCommioGetRouteBlocks = createActionExecutor('testCommioGetRouteBlocks');
export const testCommioGetStaticProfile = createActionExecutor('testCommioGetStaticProfile');
export const testCommioGetStaticProfiles = createActionExecutor('testCommioGetStaticProfiles');
export const testCommioGetTimedProfiles = createActionExecutor('testCommioGetTimedProfiles');
export const testCommioGetTollFree = createActionExecutor('testCommioGetTollFree');
export const testCommioGetWebhookToken = createActionExecutor('testCommioGetWebhookToken');
export const testCommioListRoutingProfiles = createActionExecutor('testCommioListRoutingProfiles');
export const testCommioListSmsIps = createActionExecutor('testCommioListSmsIps');
export const testCommioMockWebhook = createActionExecutor('testCommioMockWebhook');
export const testCommioOrder = createActionExecutor('testCommioOrder');
export const testCommioProcessManualPayment = createActionExecutor('testCommioProcessManualPayment');
export const testCommioRemoveSmsIp = createActionExecutor('testCommioRemoveSmsIp');
export const testCommioResetDailyInternationalSpend = createActionExecutor('testCommioResetDailyInternationalSpend');
export const testCommioRotateWebhookToken = createActionExecutor('testCommioRotateWebhookToken');
export const testCommioSearch = createActionExecutor('testCommioSearch');
export const testCommioSendMessage = createActionExecutor('testCommioSendMessage');
export const testCommioSetDefaultWebhook = createActionExecutor('testCommioSetDefaultWebhook');
export const testCommioSetDeliverySettings = createActionExecutor('testCommioSetDeliverySettings');
export const testCommioSetRouting = createActionExecutor('testCommioSetRouting');
export const testCommioSetSmsProfile = createActionExecutor('testCommioSetSmsProfile');
export const testCommioSubmitHostedNumberOrder = createActionExecutor('testCommioSubmitHostedNumberOrder');
export const testCommioSubmitTollFree = createActionExecutor('testCommioSubmitTollFree');
export const testCommioTerminationAddIp = createActionExecutor('testCommioTerminationAddIp');
export const testCommioTerminationRemoveIp = createActionExecutor('testCommioTerminationRemoveIp');
export const testCommioUpdateBillingAddress = createActionExecutor('testCommioUpdateBillingAddress');
export const testCommioUpdateNotification = createActionExecutor('testCommioUpdateNotification');
export const testCommioUpdateProfile = createActionExecutor('testCommioUpdateProfile');
export const testCommioUpdateRouteBlock = createActionExecutor('testCommioUpdateRouteBlock');
export const testCommioUpdateStaticProfile = createActionExecutor('testCommioUpdateStaticProfile');
export const testCommioUpdateTimedProfile = createActionExecutor('testCommioUpdateTimedProfile');
export const testCommioUploadBillingDocument = createActionExecutor('testCommioUploadBillingDocument');
export const testCommioUploadLoaDocument = createActionExecutor('testCommioUploadLoaDocument');
export const testCommioUploadTextNumbers = createActionExecutor('testCommioUploadTextNumbers');
// Vultr & Wazo
export const testVultrGet = createActionExecutor('testVultrGet');
export const testVultrDeploy = createActionExecutor('testVultrDeploy');
export const testVultrDestroy = createActionExecutor('testVultrDestroy');
export const testWazoAuth = createActionExecutor('testWazoAuth');
export const testWazoCalld = createActionExecutor('testWazoCalld');
export const testWazoCreateLine = createActionExecutor('testWazoCreateLine');


export const testCommioE911 = createActionExecutor('testCommioE911');
export const dispatchEmailPipeline = createActionExecutor('dispatchEmailPipeline');
export const executeSandboxSmsBroadcast = createActionExecutor('executeSandboxSmsBroadcast');
